import type { Choice, RelayView, ToViewer, VoteCandidate, Viewer } from "../src/protocol";
import { DICTIONARIES, readLocale, saveLocale, type Dictionary, type Locale } from "./i18n";
import { ago, clock, escapeHtml as esc, formatDate, length, positionNow, winnerIndex } from "./format";
import { SWITCH_COOLDOWN_MS } from "../src/room-core";

/**
 * The voting page. It decides nothing: the vote, the winner and the clock are the
 * daemon's, the room relays them, and the one thing this sends is a choice.
 *
 * One WebSocket, opened on load and reopened on its own with a backoff when it drops. The
 * room pushes the whole state on every change, so there is no polling and nothing to
 * reconcile — the latest message is the truth. The counts move optimistically for the
 * viewer's own click and the next push puts them right.
 *
 * A click is never turned away for a connection being down. The room holds a ballot until
 * the daemon has answered it, and a click made while this page's own socket is reconnecting
 * waits here and goes out the moment it is back.
 */

interface PageState {
  view: RelayView | null;
  live: boolean;
  mine: Choice | null;
  /** `mine` is held by the room for the daemon and not counted yet. */
  held: boolean;
  /** A click made while this page's socket was down, sent once it is back. */
  unsent: Choice | undefined;
  viewer: Viewer | null;
  /** The socket is open and the first state has arrived. */
  connected: boolean;
  notice: string | undefined;
  /** Daemon time minus browser time, so the countdown runs on the daemon's clock. */
  skew: number;
  /** The vote as the room last pushed it, before any optimistic click: what a refused
   *  ballot puts back. */
  confirmed: RelayView | null;
  /** Browser time until which the vote buttons rest after a click. */
  restingUntil: number;
}

const state: PageState = {
  view: null,
  live: false,
  mine: null,
  held: false,
  unsent: undefined,
  viewer: null,
  connected: false,
  notice: undefined,
  skew: 0,
  confirmed: null,
  restingUntil: 0,
};
let locale: Locale = readLocale();
let t: Dictionary = DICTIONARIES[locale];
let socket: WebSocket | undefined;
let retryMs = 1_000;

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

function connect(): void {
  const url = new URL("/api/vote/socket", location.href);
  url.protocol = location.protocol === "https:" ? "wss:" : "ws:";
  const ws = new WebSocket(url);
  socket = ws;

  ws.addEventListener("message", (event) => {
    let message: ToViewer;
    try {
      message = JSON.parse(String(event.data)) as ToViewer;
    } catch {
      return;
    }
    if (message.type === "state") {
      state.view = message.view;
      state.confirmed = message.view;
      state.live = message.live;
      state.mine = message.mine;
      state.held = message.held;
      state.viewer = message.viewer;
      state.connected = true;
      if (message.view !== null) state.skew = message.view.at - Date.now();
      retryMs = 1_000;
      sendUnsent(ws);
      render();
      return;
    }
    state.mine = message.mine;
    state.held = message.held;
    if (message.status === "refused") {
      // The click moved the counts on the spot; the ballot did not count, so they go back to
      // what the room last told us rather than waiting for a push that may not come.
      state.view = state.confirmed;
      state.notice = refusalNotice(message.reason);
    } else if (message.status === "counted") {
      state.notice = undefined;
    }
    render();
  });

  ws.addEventListener("close", () => {
    if (socket !== ws) return;
    socket = undefined;
    state.connected = false;
    renderTimers();
    setTimeout(connect, retryMs);
    retryMs = Math.min(15_000, retryMs * 2);
  });
}

function refusalNotice(reason: string | undefined): string {
  switch (reason) {
    case "cooldown":
      return t.cooldown;
    case "ended":
      return t.ended;
    case "superseded":
      return t.superseded;
    case "signed-out":
      return t.signInToVote;
    default:
      return t.closed;
  }
}

/** The click made while the socket was down, now that a fresh state says it is back. */
function sendUnsent(ws: WebSocket): void {
  const choice = state.unsent;
  state.unsent = undefined;
  if (choice === undefined || state.viewer === null || state.view?.vote.phase !== "voting") return;
  if (state.mine === choice && !state.held) return; // The room already has it counted.
  move(choice);
  ws.send(JSON.stringify({ type: "vote", choice }));
}

/** Moves this viewer's one vote on the spot; the room's next push corrects everything. */
function move(choice: Choice): void {
  const view = state.view;
  if (view === null || view.vote.phase !== "voting") return;
  const counts = [...view.vote.counts] as [number, number, number];
  if (state.mine !== null) counts[state.mine - 1] = Math.max(0, (counts[state.mine - 1] ?? 0) - 1);
  counts[choice - 1] = (counts[choice - 1] ?? 0) + 1;
  state.view = { ...view, vote: { ...view.vote, counts } };
  state.mine = choice;
}

function cast(choice: Choice): void {
  const view = state.view;
  if (view === null || view.vote.phase !== "voting") return;
  if (state.viewer === null) {
    ($("signin") as HTMLDialogElement).showModal();
    return;
  }
  if (state.mine === choice) return;
  if (Date.now() < state.restingUntil) return;

  if (socket === undefined || socket.readyState !== WebSocket.OPEN) {
    // Not refused: kept, shown as saved, and sent by `sendUnsent` when the socket is back.
    move(choice);
    state.unsent = choice;
    state.notice = undefined;
    state.restingUntil = Date.now() + SWITCH_COOLDOWN_MS;
    picked = { choice, until: Date.now() + 600 };
    render();
    setTimeout(render, SWITCH_COOLDOWN_MS + 50);
    return;
  }

  move(choice);
  state.notice = undefined;
  state.restingUntil = Date.now() + SWITCH_COOLDOWN_MS;
  picked = { choice, until: Date.now() + 600 };
  render();
  socket.send(JSON.stringify({ type: "vote", choice }));
  // Draw the buttons live again once the rest is over; the key above includes it.
  setTimeout(render, SWITCH_COOLDOWN_MS + 50);
}

const serverNow = () => Date.now() + state.skew;

// ---------------------------------------------------------------- rendering

function render(): void {
  document.documentElement.lang = locale;
  document.title = t.title;
  renderStatic();
  renderAccount();
  renderStage();
  renderTimers();
}

/** The words in the HTML shell that never depend on the vote. */
function renderStatic(): void {
  for (const button of document.querySelectorAll<HTMLButtonElement>("[data-lang]")) {
    button.setAttribute("aria-pressed", String(button.dataset.lang === locale));
  }
  $("dialog-title").textContent = t.dialog.title;
  $("dialog-body").textContent = t.dialog.body;
  $("dialog-see").textContent = t.dialog.weSee;
  $("dialog-cannot").textContent = t.dialog.weCannot;
  $("dialog-see-list").innerHTML = t.dialog.seeItems.map((item) => `<li>${esc(item)}</li>`).join("");
  $("dialog-cannot-list").innerHTML = t.dialog.cannotItems.map((item) => `<li>${esc(item)}</li>`).join("");
  $("dialog-revoke").textContent = t.dialog.revoke;
  $("dialog-not-now").textContent = t.dialog.notNow;
  $("dialog-continue").textContent = t.dialog.continue;
}

function renderAccount(): void {
  const viewer = state.viewer;
  $("account").innerHTML =
    viewer === null
      ? `<button type="button" class="btn btn-twitch" id="signin-top">${twitchGlyph()}<span class="long-label">${esc(t.signIn)}</span></button>`
      : `<div class="user">${
          viewer.profileImageUrl === "" ? "" : `<img class="avatar" src="${esc(viewer.profileImageUrl)}" alt="">`
        }<span class="user-name">${esc(viewer.name)}</span><form method="post" action="/api/vote/auth/logout"><button type="submit" class="sign-out">${esc(t.signOut)}</button></form></div>`;
}

/**
 * What the cards' markup depends on. A push that changes only the counts — most of them —
 * leaves this the same, and then the numbers move in place: rebuilding the cards on every
 * push would reload their images and could swallow a click that landed mid-rebuild.
 */
let drawnKey: string | undefined;

// ---- motion. Only where something happens, so it reads as the vote being alive rather than
// as decoration. Every animation is CSS; this only decides when a class goes on.
/** The vote whose cards have already slid in, so a redraw for a click does not replay it. */
let enteredKey: string | undefined;
/** The card just voted for, popped once on the redraw the click causes. */
let picked: { choice: Choice; until: number } | undefined;
/** The percentage each card is showing, so a push can count from it to the new one. */
const shownPercent = new Map<number, number>();
const reducedMotion = (): boolean => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Counts a percentage from what it shows to where it is going, over a third of a second. */
function tweenPercent(element: HTMLElement, from: number, to: number): void {
  if (from === to || reducedMotion()) {
    element.textContent = `${String(to)}%`;
    return;
  }
  const started = performance.now();
  const step = (now: number): void => {
    const progress = Math.min(1, (now - started) / 350);
    const eased = 1 - (1 - progress) ** 3;
    element.textContent = `${String(Math.round(from + (to - from) * eased))}%`;
    if (progress < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/** Replays a one-shot animation class even when it is already on the element. */
function replay(element: HTMLElement, className: string): void {
  element.classList.remove(className);
  void element.offsetWidth;
  element.classList.add(className);
}

function renderStage(): void {
  const view = state.view;
  const stage = $("stage");
  // A vote waiting to be counted says so, which is the only time an outage is the viewer's
  // business; otherwise a dead daemon is the general "unavailable right now".
  const waiting = state.unsent !== undefined || (state.held && (!state.live || !state.connected));
  const notice =
    state.notice ?? (waiting ? t.held : state.connected && !state.live && view !== null ? t.offline : undefined);
  const key = JSON.stringify([
    view?.voteKey,
    view?.vote.phase,
    view?.boxArt,
    state.mine,
    Date.now() < state.restingUntil,
    state.viewer?.id,
    state.connected,
    notice,
    locale,
  ]);
  if (key === drawnKey && view !== null && view.vote.phase !== "inactive") {
    updateTallies(view);
    renderOnAir(view);
    return;
  }
  drawnKey = key;

  let html = notice === undefined ? "" : `<p class="notice" role="status">${esc(notice)}</p>`;
  if (!state.connected && view === null) {
    html += `<section class="idle"><h2>${esc(t.connecting)}</h2></section>`;
  } else if (view === null || view.vote.phase === "inactive") {
    html += `<section class="idle"><h2>${esc(t.noVote)}</h2><p>${esc(t.noVoteBody)}</p></section>`;
  } else {
    if (view.vote.phase === "voting") html += `<p class="hint">${esc(t.chatHint(view.channel))}</p>`;
    // A new vote slides its cards in; a redraw of the same vote (a click, a language switch)
    // does not, or every click would replay the entrance.
    const entering = view.voteKey !== enteredKey;
    enteredKey = view.voteKey;
    html += `<div class="ballot${entering ? " ballot-enter" : ""}">${view.vote.candidates
      .map((c, i) => card(view, c, i))
      .join("")}</div>`;
  }
  stage.innerHTML = html;
  if (view !== null && view.vote.phase !== "inactive") {
    const total = view.vote.counts.reduce((sum, n) => sum + n, 0);
    view.vote.counts.forEach((count, index) => {
      shownPercent.set(index, total === 0 ? 0 : Math.round((count / total) * 100));
    });
  }
  renderOnAir(view);
}

function renderOnAir(view: RelayView | null): void {
  const playing = view?.nowPlaying;
  $("on-air").hidden = playing === undefined;
  if (playing !== undefined && view !== null) {
    ($("on-air-thumb") as HTMLImageElement).src = playing.thumbnailUrl;
    $("on-air-eyebrow").textContent = t.onAir(view.channel);
    $("on-air-title").textContent = playing.title;
    $("on-air-meta").textContent = `${t.streamed(formatDate(playing.streamedOn, locale))} · ${length(playing.durationSeconds, t.units)}`;
    $("on-air-left-label").textContent = t.leftInVod;
  }
}

function card(view: RelayView, candidate: VoteCandidate, index: number): string {
  const vote = view.vote;
  if (vote.phase === "inactive") return "";
  const choice = (index + 1) as Choice;
  const total = vote.counts.reduce((sum, n) => sum + n, 0);
  const count = vote.counts[index] ?? 0;
  const percent = total === 0 ? 0 : Math.round((count / total) * 100);
  const results = vote.phase === "cooldown";
  const won = results && winnerIndex(vote) === index;
  const lead = !results && vote.leadingIndex === index;
  const mine = state.mine === choice;
  const justPicked = picked !== undefined && picked.choice === choice && Date.now() < picked.until;
  const classes = [
    "card",
    mine && !results ? "mine" : "",
    justPicked ? "picked" : "",
    lead ? "lead" : "",
    won ? "won" : "",
    results && !won ? "lost" : "",
  ]
    .filter(Boolean)
    .join(" ");

  let button: string;
  if (results) {
    button = `<button type="button" class="btn ${won ? "btn-voted" : ""}" disabled>${esc(mine ? t.youVotedThis : won ? t.winner : "—")}</button>`;
  } else if (state.viewer === null) {
    button = `<button type="button" class="btn btn-brand" data-vote="${String(choice)}">${esc(t.signInToVote)}</button>`;
  } else if (mine) {
    button = `<button type="button" class="btn btn-voted" aria-pressed="true">✓ ${esc(t.yourVote)}</button>`;
  } else {
    // Before a vote all three are the same green call to action. Once one is cast, the
    // other two step back to a plain outline: the page should say which one is yours at a
    // glance, and three identical green buttons said nothing.
    button =
      state.mine === null
        ? `<button type="button" class="btn btn-brand" aria-pressed="false" data-vote="${String(choice)}">${esc(t.voteFor)}</button>`
        : `<button type="button" class="btn btn-switch" aria-pressed="false" data-vote="${String(choice)}"${
            Date.now() < state.restingUntil ? " disabled" : ""
          }>${esc(t.switchTo)}</button>`;
  }

  const large = candidate.thumbnailUrl.replace("/mqdefault.jpg", "/maxresdefault.jpg");
  // What a viewer will actually watch: the rerun skips intros, dances and AFK stretches.
  const onAir = candidate.watchSeconds ?? candidate.durationSeconds;
  const categories =
    candidate.categories.length === 0
      ? ""
      : `<div><p class="sect">${esc(t.categories)}</p><div class="cats">${candidate.categories
          .map((category) => {
            const art = view.boxArt[category.name];
            return `<div class="cat">${
              art === undefined || art === "" ? `<span class="cat-no-art" aria-hidden="true"></span>` : `<img src="${esc(art)}" alt="">`
            }<span><b>${esc(category.name)}</b></span></div>`;
          })
          .join("")}</div></div>`;

  return `<article class="${classes}" data-card="${String(index)}" aria-label="${esc(candidate.title)}">
    <div class="thumb">
      <img src="${esc(large)}" data-fallback="${esc(candidate.thumbnailUrl)}" alt="">
      ${won ? `<span class="stamp">${esc(t.upNextStamp)}</span>` : ""}
    </div>
    <div class="body">
      <div>
        <span class="duration">${esc(clock(onAir))}</span>
        <h2 class="title">${esc(candidate.title)}</h2>
        <div class="meta"><span>${esc(t.streamed(formatDate(candidate.streamedOn, locale)))}</span><span>${esc(
          ago(candidate.streamedOn, serverNow(), locale),
        )}</span></div>
      </div>
      ${categories}
    </div>
    <div class="foot">
      <div class="meter"><span data-meter style="width:${String(percent)}%"></span></div>
      <div class="tally"><span><b data-percent>${String(percent)}%</b> · <span data-count data-value="${String(count)}">${esc(t.votes(count))}</span></span><span class="lead-tag" data-lead ${lead ? "" : "hidden"}>${esc(t.leading)}</span></div>
      <div class="actions">${button}</div>
    </div>
  </article>`;
}

/** Moves the counts on cards already drawn, for a push that changed nothing else. */
function updateTallies(view: RelayView): void {
  const vote = view.vote;
  if (vote.phase === "inactive") return;
  const total = vote.counts.reduce((sum, n) => sum + n, 0);
  const results = vote.phase === "cooldown";
  vote.counts.forEach((count, index) => {
    const card = document.querySelector<HTMLElement>(`[data-card="${String(index)}"]`);
    if (card === null) return;
    const percent = total === 0 ? 0 : Math.round((count / total) * 100);
    const lead = !results && vote.leadingIndex === index;
    (card.querySelector("[data-meter]") as HTMLElement).style.width = `${String(percent)}%`;
    const percentEl = card.querySelector("[data-percent]") as HTMLElement;
    tweenPercent(percentEl, shownPercent.get(index) ?? percent, percent);
    const countEl = card.querySelector("[data-count]") as HTMLElement;
    const previous = Number(countEl.dataset.value ?? count);
    countEl.textContent = t.votes(count);
    countEl.dataset.value = String(count);
    // A card that just gained votes says so for a moment; losing them is not news.
    if (count > previous) replay(percentEl, "bump");
    shownPercent.set(index, percent);
    (card.querySelector("[data-lead]") as HTMLElement).hidden = !lead;
    card.classList.toggle("lead", lead);
  });
}

/** The parts that move every second: the clock, the drain bar, the on-air position. */
function renderTimers(): void {
  const view = state.view;
  const bar = $("vote-bar");
  const now = serverNow();

  const playing = view?.nowPlaying;
  if (playing !== undefined) {
    const position = positionNow(playing, now);
    $("on-air-progress").style.width =
      position === undefined ? "0%" : `${String((position / Math.max(1, playing.durationSeconds)) * 100)}%`;
    $("on-air-end").hidden = position === undefined;
    if (position !== undefined) $("on-air-left").textContent = clock(playing.durationSeconds - position);
  }

  const vote = view?.vote;
  if (vote === undefined || vote.phase === "inactive") {
    bar.hidden = true;
    return;
  }
  bar.hidden = false;
  const total = vote.counts.reduce((sum, n) => sum + n, 0);

  if (vote.phase === "cooldown") {
    const winner = winnerIndex(vote);
    const percent = total === 0 ? 0 : Math.round(((vote.counts[winner] ?? 0) / total) * 100);
    $("vote-title").textContent = t.upNext(vote.candidates[winner]?.title ?? "");
    $("vote-sub").textContent = t.wonWith(percent, t.votes(total));
    $("clock-label").textContent = t.startingNow;
    $("clock-time").textContent = "";
    $("drain").hidden = true;
    bar.classList.remove("urgent");
    return;
  }

  const remaining = Math.max(0, vote.phaseEndsAt - now);
  const span = Math.max(1, vote.phaseEndsAt - vote.phaseStartsAt);
  $("vote-title").textContent = t.pickNext;
  $("vote-sub").textContent = t.votingSub(t.votes(total));
  $("clock-label").textContent = t.closesIn;
  $("clock-time").textContent = clock(remaining / 1_000);
  $("drain").hidden = false;
  ($("drain").firstElementChild as HTMLElement).style.width = `${String(Math.min(100, (remaining / span) * 100))}%`;
  bar.classList.toggle("urgent", remaining <= 60_000);
}

function twitchGlyph(): string {
  return '<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M4.3 2 3 5.4v13.5h4.6V22h2.6l3.1-3.1h3.7L22 13.9V2H4.3Zm15.9 11-2.9 2.9h-4.6L9.6 18.4v-2.5H5.7V3.8h14.5V13Zm-2.9-5.9v5h-1.7v-5h1.7Zm-4.6 0v5H11v-5h1.7Z"/></svg>';
}

// ---------------------------------------------------------------- wiring

document.addEventListener("click", (event) => {
  const target = event.target as HTMLElement;
  const vote = target.closest<HTMLElement>("[data-vote]");
  if (vote !== null) {
    cast(Number(vote.dataset.vote) as Choice);
    return;
  }
  if (target.closest("#signin-top") !== null) {
    ($("signin") as HTMLDialogElement).showModal();
    return;
  }
  if (target.closest("#dialog-not-now") !== null) {
    ($("signin") as HTMLDialogElement).close();
    return;
  }
  const lang = target.closest<HTMLElement>("[data-lang]");
  if (lang !== null) {
    locale = lang.dataset.lang === "en" ? "en" : "ko";
    t = DICTIONARIES[locale];
    saveLocale(locale);
    render();
  }
});

// Images are not for dragging out of the page (Firefox ignores -webkit-user-drag).
document.addEventListener("dragstart", (event) => {
  if (event.target instanceof HTMLImageElement) event.preventDefault();
});

// The full-size thumbnail is not on every upload; fall back to the one the daemon sent.
document.addEventListener(
  "error",
  (event) => {
    const image = event.target;
    if (image instanceof HTMLImageElement && image.dataset.fallback !== undefined && image.src !== image.dataset.fallback) {
      image.src = image.dataset.fallback;
    }
  },
  true,
);

const problem = new URLSearchParams(location.search).get("signin");
if (problem === "unconfigured" || problem === "expired" || problem === "failed") {
  state.notice = t.signInProblem[problem];
  history.replaceState(null, "", location.pathname);
}

render();
connect();
setInterval(renderTimers, 1_000);
